import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { RefreshCw, Loader2, AlertTriangle } from "lucide-react";

const Tile = ({ label, value, sub, warn }) => (
  <div className={`bg-white border p-4 ${warn ? "border-taven-orange" : "border-black/10"}`}
       data-testid={`health-tile-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-2xl font-black font-display">{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function AdminCatalogHealth() {
  const client = adminClient();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    client.get("/admin/catalog/health").then(({ data }) => setD(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!d) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading catalog health…</div>;
  const j = d.jobs || {}, s = d.stock || {}, dup = d.duplicates || {}, b = d.cj_budget_today || {};
  return (
    <div data-testid="admin-catalog-health">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black font-display">Catalog &amp; Sync Health</h2>
        <button onClick={load} data-testid="health-refresh" className="flex items-center gap-1.5 text-xs font-bold border border-black/15 px-3 py-2 hover:bg-taven-gray">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Read-only. No CJ live calls, no Shopify writes. Generated {new Date(d.generated_at).toLocaleString()}.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile label="Shopify Mirror" value={d.shopify_mirror_total} />
        <Tile label="CJ Catalog" value={d.cj_catalog_total} />
        <Tile label="Open Exceptions" value={d.open_exceptions} warn={d.open_exceptions > 0} />
        <Tile label="Dead-letter Jobs" value={j.dead || 0} warn={(j.dead || 0) > 0} />
      </div>

      <h3 className="text-sm font-black uppercase tracking-wider mb-2">Sync Jobs</h3>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
        {["queued", "failed", "retrying", "dead", "done", "rolled_back"].map((k) => (
          <Tile key={k} label={k} value={j[k] || 0} warn={(k === "dead" || k === "failed") && (j[k] || 0) > 0} />
        ))}
      </div>

      <h3 className="text-sm font-black uppercase tracking-wider mb-2">Stock Verification (CJ)</h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Tile label="Positive" value={s.positive || 0} />
        <Tile label="Zero" value={s.zero || 0} warn={(s.zero || 0) > 0} />
        <Tile label="Null Unknown" value={s.null_unknown || 0} warn={(s.null_unknown || 0) > 0} />
        <Tile label="Fresh" value={s.fresh || 0} />
        <Tile label="Stale" value={s.stale || 0} warn={(s.stale || 0) > 0} />
      </div>

      <h3 className="text-sm font-black uppercase tracking-wider mb-2">Duplicates &amp; Budget</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Tile label="CJ pid dups" value={dup.cj_pid || 0} warn={(dup.cj_pid || 0) > 0} />
        <Tile label="Shopify handle dups" value={dup.shopify_handle || 0} warn={(dup.shopify_handle || 0) > 0} />
        <Tile label="CJ Budget Today" value={`${b.points || 0}/${b.daily_budget || 0}`} sub={`${b.calls || 0} calls · ${b.day || ""}`} warn={(b.points || 0) >= (b.daily_budget || 1)} />
        <Tile label="Last Sync" value={d.last_sync ? new Date(d.last_sync).toLocaleDateString() : "—"} />
      </div>

      <div className="bg-taven-gray/40 border border-black/10 p-4 text-xs">
        <p className="font-bold flex items-center gap-1.5 mb-2"><AlertTriangle className="w-3.5 h-3.5 text-taven-orange" /> Live Flags (fail-closed)</p>
        <div className="flex flex-wrap gap-2" data-testid="health-flags">
          {Object.entries(d.flags || {}).map(([k, v]) => (
            <span key={k} className="px-2 py-1 bg-white border border-black/10">{k}=<b>{String(v)}</b></span>
          ))}
        </div>
      </div>
    </div>
  );
}
